import { Box, MenuItem, Stack, Typography } from "@mui/material";
import FullScreenDialog, { FullScreenDialogCloseReason } from "../../../../components/full-screen-dialog/FullScreenDialog";
import { bindDialog, BindDialogReturn, DialogProps, DialogResult, DialogState, useDialogState } from "../../../../hooks/use-dialog-state/UseDialogState";
import Form from "../../../forms/form/Form";
import UnsavedChangesPrompt from "../../../unsaved-changes-prompt/UnsavedChangesPrompt";
import { isMobile, isTablet } from "react-device-detect";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useConfirmation } from "../../../../contexts/confirmation-context/ConfirmationContext";
import { useUserContext } from "../../../../contexts/user-context/UserContext";
import { useQueryNotifications } from "../../../../queries/UseQueryNotifications";
import { useGroup } from "../../../../queries/groups/UseGroup";
import { ScheduleFormType } from "../TierEditorUtils";
import { useEffect, useState, type JSX } from "react";
import SettingsBar, { SettingsActionEnum } from "../../../settings-bar/SettingsBar";
import FormProperty from "../../../forms/form-property/FormProperty";
import FormSelect from "../../../forms/form-select/FormSelect";
import { useAlarmSchemeReferenceData } from "../../../../queries/alarm-scheme-admin/UseAlarmSchemeReferenceData";
import FormPropertySection from "../../../forms/form-property-section/FormPropertySection";
import { eLynxObjectTypeEnum } from "../../../../common/enums/eLynxObjectType";
import { getObjectTypeName } from "../../../../common/utilities/GetObjectTypeName";
import FormNotificationLists from "../../../form-notification-lists/FormNotificationLists";
import { IReferenceNotificationListDTO } from "../../../../services/setpoint-administration-service/Models";
import OrganizationPicker from "../../../organization-picker/OrganizationPicker";
import SideSheetFilterInput from "../../../side-sheet/side-sheet-filter-input/SideSheetFilterInput";
import SideSheetSwitch from "../../../side-sheet/side-sheet-switch/SideSheetSwitch";
import SideSheet from "../../../side-sheet/side-sheet/SideSheet";
import { useFormNotificationListsState } from "../../../form-notification-lists/FormNotificationListsState";
import { IOrganization } from "../../../../services/organization-data-service/Models";
import { ContextActionMenu } from "../../../contextual-app-bar/ContextActionMenu";
import { ALWAYS_SCHEDULE } from "../../../../services/alarm-scheme-data-service/Models";

export type ScheduledNotificationEditorDialogInitValues = {
    groupID: number,
    organizationID: number,
    item: ScheduleFormType
};

export type ScheduledNotificationEditorDialogResultValues = {
    schedule: ScheduleFormType | undefined;
};

export type ScheduledNotificationEditorDialogResultReason = FullScreenDialogCloseReason | "cancel" | "save";

export type ScheduledNotificationEditorDialogResult = DialogResult<ScheduledNotificationEditorDialogResultValues, ScheduledNotificationEditorDialogResultReason>;

export type ScheduledNotificationEditorDialogProps = DialogProps<ScheduledNotificationEditorDialogInitValues, ScheduledNotificationEditorDialogResult>;

export default function ScheduledNotificationEditorDialog({
    open,
    onClose,
    values
}: ScheduledNotificationEditorDialogProps): JSX.Element {

    const isPhone = isMobile && !isTablet;
    const [userState] = useUserContext();
    const { confirm
        //, acknowledge 
    } = useConfirmation();
    const { t } = useTranslation();
        const [openSideSheet, setOpenSideSheet] = useState(isPhone ? false: true);
    
    const{ state,
        setNotificationListFilterText,
        setOrganization,
        setViewSelected,
        resetState
    } = useFormNotificationListsState();

    const {data: group } = useQueryNotifications(useGroup)({
        customerID: userState.customer?.id as number,
        groupID: values?.groupID as number,
        enabled: values !== undefined
    });

    const {data: referenceData} = useQueryNotifications(useAlarmSchemeReferenceData)({
        groupID: values?.groupID as number,
        //organizationID: group?.organizationID as number,
        organizationID: values?.organizationID as number,
        enabled: values !== undefined && group !== undefined
    });

    const form = useForm<ScheduleFormType>({
        //mode: "all",
        mode: 'onChange',
        defaultValues: {
            id: -1,
            name: '',
            selected: false,
            notificationLists: []
        }
    });

    const {
        getValues,
        watch,
        formState: {
            //dirtyFields,
            isDirty,
            isValid,
            //defaultValues
            //touchedFields
        },
        reset,
        //setValue,
        control,
        handleSubmit,
    } = form;
    
    const scheduleForm = useWatch({ control: control }) as ScheduleFormType;

    const [
        scheduleID,
        notificationLists, 
    ] = watch([
        'id',
        'notificationLists' 
    ]);


    const selectedNotificationListCount = notificationLists.filter(o => o.selected).length;
    const enableViewSelected = selectedNotificationListCount > 0;
    const availableSchedules = referenceData?.schedules.filter(o => o.id !== ALWAYS_SCHEDULE);


    async function onOkay(formValues: ScheduleFormType) {

        const values = getValues();

        const notifications = values.notificationLists.filter(o => o.selected);

        onClose && onClose({ 
            values: {
                schedule: {...values,
                    name: referenceData?.schedules.find(o => o.id === values?.id)?.name ?? '',
                    notificationLists: notifications
                }
            },
            reason: "save" 
        });
    
    }

    async function onCancel() {

        const canCancel = isDirty
            ? await getUserConfirmation()
            : true;

        if (canCancel) {
            onClose && onClose({ 
                values: {
                    schedule: scheduleForm
                },
                reason: "cancel" 
            });
        }
    }

    async function getUserConfirmation() {

        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText: t("cancelConfirmation.confirmationText"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true,
            showCloseButton: true
        })) === "yes";
    }

    function onOrganizationChanged(item: IOrganization) {
        setOrganization(item.id);
    }

    function onViewSelected(value: boolean) {
        setViewSelected(value);
    }

    function onSetFilter(value: string) {
        setNotificationListFilterText(value);
    }

    useEffect(() => {

        if (values?.item && referenceData) {

            const formValue = {...values.item
            } as ScheduleFormType;

            const notificationLists: IReferenceNotificationListDTO[] = [];

            referenceData.organizations.forEach(o => {
                o.notificationLists.forEach(v => {

                    notificationLists.push({
                        id: v.id,
                        name: v.name,
                        organizationID: o.id,
                        selected: formValue.notificationLists.find(z => z.id === v.id) ? true : false
                    } as IReferenceNotificationListDTO );
                })
            });

            formValue.notificationLists = notificationLists;
            reset(formValue);
        }
    }, [
        values?.item,
        reset,
        referenceData
    ]);


    return (
        <FullScreenDialog open={open} onClose={reason => onClose({ reason: reason })}>
            <SettingsBar 
                title={t((values?.item.id ?? 0)> 0 ? 
                        'alarmSchemeAdmin.tiers.buttons.editScheduledNotification' : 
                        'alarmSchemeAdmin.tiers.buttons.addScheduledNotification')  
                } 
                
                onClose={
                    async (reason: SettingsActionEnum) => {
                        reason === SettingsActionEnum.OK
                            ? handleSubmit(async (data) => onOkay(data))()
                            : onCancel();
                    }}
                modified={isDirty && isValid && scheduleID > 0} />
            {values?.item && 
            <Form form={form} 
                sx={{ 
                    paddingLeft: 1 

                }} 
                hideFormScroll={true}>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end'
                    }}>
                    <ContextActionMenu 
                        viewOptionState={state.viewOptionState}
                        //actions={recipientActionItems}
                        //onExecuteAction={onExecuteAction}
                        hideOptionsAction={!isPhone}
                        onOverrideViewOptionsClick={() => {setOpenSideSheet(!openSideSheet)}}
                        buttonColor="default"
                        hideAddToFavoritesAction={true}
                        />
                </Box>

                <Stack
                    direction='row'
                    sx={{
                        display: 'flex',
                        flex: '1 1 auto'
                    }}>

                    <Box
                        sx={{
                        position: 'relative',
                        display: 'flex',
                        flexDirection: "column",
                        flex: '1 1 auto',
                        overflow: "hidden",
                        height: "100%",
                        }}>


                        <FormPropertySection label='' sx={{marginTop: 1}}>
                            <FormProperty>
                                <FormSelect
                                    ariaLabel="schedule"
                                    name="id"
                                    label={t('properties.alarmSchedule')}
                                    sx={{width: '100%'}}
                                    rules={{
                                        required: true,
                                    }}
                                    readOnly={scheduleForm.id > 0}
                                >

                                    <MenuItem value={-1} disabled={true}>
                                    </MenuItem>

                                    {availableSchedules?.map((o, index) =>
                                        <MenuItem key={index} value={o.id}>
                                            {o.name}
                                        </MenuItem>)}
                                </FormSelect>
                            </FormProperty>

                        </FormPropertySection>


                        
                        <Typography variant="subtitle1" sx={{
                            fontWeight: 600
                        }} >
                            {getObjectTypeName(eLynxObjectTypeEnum.NotificationList, true)}
                        </Typography>

                        <FormNotificationLists visible={true}
                            //ref={notificationList}
                            groupID={values?.groupID as number}
                            filterText={state.notificationListFilterText}
                            viewSelected={state.viewSelected}
                            organizationID={state.organizationID === -1 ? undefined : state.organizationID} 
                            allowEdit={false}
                        />
                    </Box>



                    <SideSheet
                        title={t("options")}
                        open={openSideSheet}
                        hideCloseButton={!isPhone}
                        onClose={() => setOpenSideSheet(false)}
                        onReset={resetState}
                    >

                        <OrganizationPicker 
                            label={getObjectTypeName(eLynxObjectTypeEnum.Organization)}
                            color="inherit"
                            //organizationID={state.organizationID ?? group?.organizationID as number} 
                            organizationID={state.organizationID ?? -1 } 
                            onSelected={onOrganizationChanged}
                            includeAllOption={true}
                            />


                        <SideSheetSwitch
                            label={t("setpointAdmin.viewSelected", {name: getObjectTypeName(eLynxObjectTypeEnum.NotificationList,  true)})}
                            checked={state.viewSelected}
                            onChange={(checked: boolean) => {
                                onViewSelected(checked);
                            }}
                            disabled={!enableViewSelected} />

                        <SideSheetFilterInput
                            label={t("setpointAdmin.filterByObject", {objectType: getObjectTypeName(eLynxObjectTypeEnum.NotificationList)})}
                            value={
                                state.notificationListFilterText}
                            onChange={(value) => {
                                onSetFilter(value);
                            }} 
                            disabled={state.viewSelected}/>

                    </SideSheet>
                </Stack>
            </Form>
            }
            <UnsavedChangesPrompt
                hasChanges={isDirty}
                getUserConfirmation={getUserConfirmation}
            />
        </FullScreenDialog>
    );
}

export type ScheduledNotificationEditorDialogState = DialogState<ScheduledNotificationEditorDialogInitValues, ScheduledNotificationEditorDialogResult>;
export type BindScheduledNotificationEditorDialogReturn = BindDialogReturn<ScheduledNotificationEditorDialogInitValues, ScheduledNotificationEditorDialogResult>;

export function useScheduledNotificationEditorDialogState(): ScheduledNotificationEditorDialogState {
    return useDialogState();
}

export function bindScheduledNotificationEditorDialog(props: ScheduledNotificationEditorDialogState): BindScheduledNotificationEditorDialogReturn {
    return bindDialog(props);
}
